import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { RedirectIfAuth, RequireAuth } from '@/features/auth/guards';
import { AdminAuthGate } from '@/features/auth/AdminAuthGate';
import { I18nProvider } from '@/features/i18n/I18nProvider';
import { AdminShell, UserShell } from '@/layouts/AppShells';
import { AdminReferralsPage } from '@/pages/admin/AdminReferralsPage';
import { ContactPage, LandingPage, PresentationPage } from '@/pages/marketing/MarketingPages';
import { PrivacyPolicyPage, TermsOfUsePage } from '@/pages/marketing/LegalPages';
import { MarketplacePage } from '@/pages/marketing/MarketplacePage';
import { ProductDetailPage } from '@/pages/marketing/ProductDetailPage';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from '@/pages/auth/AuthPages';
import { AuthCallbackPage } from '@/pages/auth/AuthCallbackPage';
import { OAuthReturnFix } from '@/features/auth/OAuthReturnFix';
import { DashboardPage } from '@/pages/user/DashboardPage';
import { ConceptionPage } from '@/pages/user/ConceptionPage';
import { PlanificationPage } from '@/pages/user/PlanificationPage';
import { PilotagePage } from '@/pages/user/PilotagePage';
import { ProfilePage, ProjectsPage } from '@/pages/user/AccountPages';
import { ConcoursHubPage } from '@/pages/user/ConcoursHubPage';
import { ContestDocumentationPage } from '@/pages/user/ContestDocumentationPage';
import { ContestEvolutionPage } from '@/pages/user/ContestEvolutionPage';
import { ContestInscriptionPage } from '@/pages/user/ContestInscriptionPage';
import { ChangePasswordPage } from '@/pages/user/ChangePasswordPage';
import { NetworkPage } from '@/pages/user/NetworkPage';
import {
  NetworkCommunitiesPage,
  NetworkGuidesPage,
  NetworkMasterclassPage,
} from '@/features/network/NetworkResourcePages';
import { CrmPage } from '@/pages/user/CrmPage';
import { BudgetPage } from '@/pages/user/BudgetPage';
import { UserMarketplacePage } from '@/pages/user/UserMarketplacePage';
import { MarketplaceCrmHubPage } from '@/pages/user/MarketplaceCrmHubPage';
import { FidelisationPage } from '@/pages/user/FidelisationPage';
import { AssistantMarketingPage } from '@/pages/user/AssistantMarketingPage';
import { SubscriptionPage } from '@/pages/user/SubscriptionPage';
import { AccompagnementPage } from '@/pages/user/AccompagnementPage';
import { PreoccupationsPage } from '@/features/preoccupations/PreoccupationsPage';
import {
  AdminAgendaPage,
  AdminAssistancePage,
  AdminContactPage,
  AdminConcoursPage,
  AdminDashboardPage,
  AdminExploitationPage,
  AdminMembersPage,
  AdminUsersPage,
} from '@/pages/admin/AdminPages';
import { AdminAccompagnementPage } from '@/pages/admin/AdminAccompagnementPage';
import { AdminMarketplacePage } from '@/pages/admin/AdminMarketplacePage';
import { AdminCommunitiesPage } from '@/pages/admin/AdminCommunitiesPage';
import { AdminNetworkPage } from '@/pages/admin/AdminNetworkPage';
import { BrandReviewPage } from '@/pages/dev/BrandReviewPage';
import { NativeBootstrap } from '@/features/native/NativeBootstrap';
import { ToastProvider } from '@/features/ui/toast/ToastProvider';
import { AnalyticsRoot } from '@/features/analytics/AnalyticsRoot';
import { isNativeApp } from '@/lib/nativeApp';
import { AdminAudiencePage } from '@/pages/admin/AdminAudiencePage';

export function AppRouter() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <I18nProvider>
        <ToastProvider>
        <NativeBootstrap />
        <OAuthReturnFix />
        <AnalyticsRoot />
        <Routes>
          {/* APK : jamais la landing marketing — redirection synchrone (évite le flash 1s) */}
          <Route
            path="/"
            element={isNativeApp() ? <Navigate to="/connexion" replace /> : <LandingPage />}
          />
          <Route path="/presentation" element={<PresentationPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/:id" element={<ProductDetailPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/politique-de-confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/conditions-d-utilisation" element={<TermsOfUsePage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          {import.meta.env.DEV ? <Route path="/dev/charte" element={<BrandReviewPage />} /> : null}

          <Route element={<RedirectIfAuth />}>
            <Route path="/connexion" element={<LoginPage />} />
            <Route path="/inscription" element={<RegisterPage />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
            <Route path="/app" element={<UserShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="conception" element={<ConceptionPage />} />
              <Route path="planification" element={<PlanificationPage />} />
              <Route path="pilotage" element={<PilotagePage />} />
              <Route path="accompagnement" element={<AccompagnementPage />} />
              <Route path="reseau" element={<NetworkPage />} />
              <Route path="reseau/guides" element={<NetworkGuidesPage />} />
              <Route path="reseau/masterclass" element={<NetworkMasterclassPage />} />
              <Route path="reseau/communautes" element={<NetworkCommunitiesPage />} />
              <Route path="marketplace-crm" element={<MarketplaceCrmHubPage />} />
              <Route path="marketplace-crm/marketplace" element={<UserMarketplacePage />} />
              <Route path="marketplace-crm/crm" element={<CrmPage />} />
              <Route path="marketplace-crm/fidelisation" element={<FidelisationPage />} />
              <Route path="marketplace-crm/assistant-marketing" element={<AssistantMarketingPage />} />
              <Route path="crm" element={<Navigate to="/app/marketplace-crm/crm" replace />} />
              <Route path="marketplace" element={<Navigate to="/app/marketplace-crm/marketplace" replace />} />
              <Route path="budget" element={<BudgetPage />} />
              <Route path="concours" element={<ConcoursHubPage />} />
              <Route path="concours/documentation" element={<ContestDocumentationPage />} />
              <Route path="concours/inscription" element={<ContestInscriptionPage />} />
              <Route path="concours/evolution" element={<ContestEvolutionPage />} />
              <Route path="assistance" element={<PreoccupationsPage />} />
              <Route path="projets" element={<ProjectsPage />} />
              <Route path="abonnement" element={<SubscriptionPage />} />
              <Route path="profil" element={<ProfilePage />} />
              <Route path="mot-de-passe" element={<ChangePasswordPage />} />
            </Route>
          </Route>

          <Route element={<AdminAuthGate />}>
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="audience" element={<AdminAudiencePage />} />
              <Route path="utilisateurs" element={<AdminUsersPage />} />
              <Route path="membres" element={<AdminMembersPage />} />
              <Route path="exploitation" element={<AdminExploitationPage />} />
              <Route path="agenda" element={<AdminAgendaPage />} />
              <Route path="concours" element={<AdminConcoursPage />} />
              <Route path="reseau" element={<AdminNetworkPage />} />
              <Route path="assistance" element={<AdminAssistancePage />} />
              <Route path="accompagnement" element={<AdminAccompagnementPage />} />
              <Route path="marketplace" element={<AdminMarketplacePage />} />
              <Route path="parrainage" element={<AdminReferralsPage />} />
              <Route path="communautes" element={<AdminCommunitiesPage />} />
              <Route path="contact" element={<AdminContactPage />} />
            </Route>
          </Route>

          <Route
            path="*"
            element={<Navigate to={isNativeApp() ? '/connexion' : '/'} replace />}
          />
        </Routes>
        </ToastProvider>
        </I18nProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
